import { useMemo, useRef, useState } from "react"
import { useBoundContext } from "lib/@components/binding/use-bound-context"
import { useParams } from "react-router"
import { useScheduleEditorSeed } from "../../hooks/use-schedule-editor-seed"
import { PAYLOAD_STATUS_APPROVED, PAYLOAD_STATUS_REJECTED } from "routes/audit/lib/audit-constants.mjs"
import { getDraftAmendsByReferenceIdAndType } from "authoring/routes/project/amends/controller/get-draft-amends-by-reference-id-and-type"
import { refreshOnlyFor } from "lib/graphql/api"
import { createDraftId } from "authoring/routes/project/lib/project-utils"
import { AuthoringAmendsType } from "authoring/lib/enum/authoring-amends-type.mjs"
import { AcceptedChanged, IsAccepted } from "event-definitions"
import { omit } from "lib/omit"
import { useNewScheduleReviewApprovalState } from "./use-new-schedule-review-approval-state"

export function useReviewApprovalState() {
    const { projectId } = useParams()
    const { reviewReferenceId, reviewCycle, scheduleId } = useBoundContext()
    const { seedSource } = useScheduleEditorSeed()
    const [acceptedById, setAcceptedById] = useState({})
    const registeredIds = useRef({})

    AcceptedChanged.useEvent(handleAcceptedChanged)
    // Mirror compare/save-button behavior: provide accepted state for IsAccepted.call()
    // so difference cards can render accepted/not-accepted in review mode.
    IsAccepted.useEvent(handleIsAccepted, [acceptedById])

    const { data: amends, loading: amendsLoading } =
        getDraftAmendsByReferenceIdAndType.useResults.status(
            reviewReferenceId,
            AuthoringAmendsType.AUTHORING_AMENDS,
            { "payload.groupReviewId": reviewReferenceId },
            projectId,
            refreshOnlyFor(createDraftId(projectId, scheduleId))
        ) ?? {}

    useNewScheduleReviewApprovalState({
        amends,
        amendsLoading,
        projectId,
        reviewCycle,
        scheduleId,
        seedSource,
        setAcceptedById,
    })

    const decisions = useMemo(() => Object.entries(acceptedById), [acceptedById])
    const trackedChangeCount = decisions.length
    const allApproved = trackedChangeCount > 0 && decisions.every(([, value]) => value?.accepted === true)
    const amendIdsToCheck = useMemo(() => getAmendIdsToCheck(decisions, amends ?? []), [decisions, amends])

    return {
        allApproved,
        trackedChangeCount,
        amendIdsToCheck,
    }

    function handleAcceptedChanged({ id, taskId, accepted = null, uniqueId, auditId = null }) {
        if (!id) return
        if (accepted === undefined) {
            if (registeredIds.current[id] === uniqueId) {
                delete registeredIds.current[id]
                setAcceptedById((current) => omit(current, id))
            }
            return
        }
        registeredIds.current[id] = uniqueId
        setAcceptedById((current) => ({ ...current, [id]: { accepted, auditId, taskId } }))
    }

    function handleIsAccepted(props) {
        if (!props?.id) return
        props.isAccepted = acceptedById[props.id]?.accepted
    }
}

function getAmendIdsToCheck(decisions, amends) {
    const latestAmendByReviewId = getLatestAmendByReviewId(amends)
    const amendIds = new Set()
    for (const [id, decision] of decisions) {
        const { accepted, auditId, taskId } = decision ?? {}
        if (
            auditId &&
            matchesAcceptedDecision(
                { payload: { status: accepted ? PAYLOAD_STATUS_APPROVED : PAYLOAD_STATUS_REJECTED } },
                accepted
            )
        ) {
            amendIds.add(auditId)
            continue
        }
        const amend = (taskId && latestAmendByReviewId.get(taskId)) ?? latestAmendByReviewId.get(id)
        if (!amend) continue
        if (!matchesAcceptedDecision(amend, accepted)) continue
        const amendId = amend?.id
        if (!amendId) continue
        amendIds.add(amendId)
    }
    return Array.from(amendIds)
}

function getLatestAmendByReviewId(amends) {
    const latestAmendByReviewId = new Map()
    for (const amend of amends) {
        const taskId = amend?.payload?.taskId
        if (taskId && !latestAmendByReviewId.has(taskId)) {
            latestAmendByReviewId.set(taskId, amend)
        }
        const taskTitle = amend?.payload?.taskTitle
        if (taskTitle && !latestAmendByReviewId.has(taskTitle)) {
            latestAmendByReviewId.set(taskTitle, amend)
        }
        const amendsKey = amend?.payload?.amendsKey
        if (amendsKey && !latestAmendByReviewId.has(amendsKey)) {
            latestAmendByReviewId.set(amendsKey, amend)
        }
    }
    return latestAmendByReviewId
}

function matchesAcceptedDecision(amend, accepted) {
    const status = amend?.payload?.status
    if (accepted === true) return status === PAYLOAD_STATUS_APPROVED
    if (accepted === false) return status === PAYLOAD_STATUS_REJECTED
    return false
}
